// src/admin/InventoryPage.js
import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../config';
import AddInventoryItemForm from './AddInventoryItemForm';
import InventoryTable from './InventoryTable';
import MovementModal from './MovementModal';
import HistoryModal from './HistoryModal';

export default function InventoryPage() {
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState(null);

  const [inventoryItems, setInventoryItems] = useState([]);
  const [isMovementModalOpen, setMovementModalOpen] = useState(false);
  const [movementItem, setMovementItem] = useState(null);
  const [isHistoryModalOpen, setHistoryModalOpen] = useState(false);
  const [historyItem, setHistoryItem] = useState(null);

  // Token e base URL
  const token = localStorage.getItem('access_token');

  // Al mount, carichiamo la lista dei ristoranti per la company dell’utente
  useEffect(() => {
    fetchRestaurants();
    // eslint-disable-next-line
  }, []);

  // Se cambiamo ristorante, ricarichiamo l’inventario
  useEffect(() => {
    if (selectedRestaurantId) {
      fetchInventory(selectedRestaurantId);
    }
  }, [selectedRestaurantId]);

  // ---------------------------------------
  //              API CALLS
  // ---------------------------------------

  async function fetchRestaurants() {
    // Endpoint /restaurants restituisce i ristoranti di quell’admin (stessa company)
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/restaurants`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Errore caricamento ristoranti');
      const data = await res.json();
      setRestaurants(data);
      // Se vuoi selezionare di default il primo ristorante, ad es.:
      if (data.length > 0) {
        setSelectedRestaurantId(data[0].id);
      }
    } catch (err) {
      console.error('fetchRestaurants error:', err);
      alert(err.message);
    }
  }

  async function fetchInventory(restaurantId) {
    try {
      const url = `${process.env.REACT_APP_API_URL}/restaurant_ingredients?restaurant_id=${restaurantId}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Errore GET /restaurant_ingredients');
      }
      const data = await response.json();
      setInventoryItems(data);
    } catch (error) {
      console.error('fetchInventory error:', error);
      alert(error.message);
    }
  }

  // Chiamata dopo la creazione di un nuovo prodotto
  const handleNewInventoryItem = () => {
    if (selectedRestaurantId) {
      fetchInventory(selectedRestaurantId);
    }
  };

  // Movimenti (apri e chiudi modale)
  const openMovementModal = (item) => {
    setMovementItem(item);
    setMovementModalOpen(true);
  };
  const handleMovementSuccess = () => {
    setMovementModalOpen(false);
    setMovementItem(null);
    if (selectedRestaurantId) {
      fetchInventory(selectedRestaurantId);
    }
  };

  // Storico
  const openHistoryModal = (item) => {
    setHistoryItem(item);
    setHistoryModalOpen(true);
  };

  // Eliminazione di un RestaurantIngredient
  const handleDeleteItem = async (item) => {
    if (item.quantity > 0) {
      alert(
        `Impossibile eliminare "${item.ingredient_name}" perché la quantità (${item.quantity}) è ancora > 0.\n` +
          `Per favore, esegui prima un movimento per azzerarla.`
      );
      return;
    }
    const confirmDelete = window.confirm(
      `Sei sicuro di voler eliminare "${item.ingredient_name}" (ID: ${item.id})?`
    );
    if (!confirmDelete) return;

    try {
      const url = `${process.env.REACT_APP_API_URL}/restaurant_ingredients/${item.id}`;
      const res = await fetch(url, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const errorData = await res.json();
        const msg = errorData.detail || 'Errore durante l’eliminazione.';
        alert(msg);
        return;
      }

      alert(`Prodotto "${item.ingredient_name}" eliminato con successo.`);
      if (selectedRestaurantId) {
        fetchInventory(selectedRestaurantId);
      }
    } catch (error) {
      console.error('handleDeleteItem error:', error);
      alert('Errore di rete o server.');
    }
  };

  // ---------------------------------------
  //              RENDER
  // ---------------------------------------

  return (
    <div style={styles.pageWrapper}>
      <div style={styles.card}>
        <h1 style={styles.title}>Gestione Inventario</h1>

        {/* Selettore Ristorante */}
        <div style={styles.selectRow}>
          <label htmlFor="restaurantSelect" style={styles.selectLabel}>
            Seleziona Ristorante:
          </label>
          <select
            id="restaurantSelect"
            style={styles.select}
            value={selectedRestaurantId || ''}
            onChange={(e) => setSelectedRestaurantId(e.target.value)}
          >
            {restaurants.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name} (ID: {r.id})
              </option>
            ))}
          </select>
        </div>

        {/* Form creazione prodotto: passiamo selectedRestaurantId */}
        <AddInventoryItemForm
          restaurantId={selectedRestaurantId}
          onSuccess={handleNewInventoryItem}
        />

        <InventoryTable
          items={inventoryItems}
          onOpenMovement={openMovementModal}
          onOpenHistory={openHistoryModal}
          onDeleteItem={handleDeleteItem}
        />
      </div>

      {isMovementModalOpen && movementItem && (
        <MovementModal
          item={movementItem}
          onClose={() => setMovementModalOpen(false)}
          onSuccess={handleMovementSuccess}
        />
      )}

      {isHistoryModalOpen && historyItem && (
        <HistoryModal
          item={historyItem}
          onClose={() => setHistoryModalOpen(false)}
        />
      )}
    </div>
  );
}

const styles = {
  pageWrapper: {
    minHeight: '100vh',
    width: '100%',
    backgroundColor: '#FAFAFA',
    display: 'flex',
    justifyContent: 'center',
    padding: '30px',
    boxSizing: 'border-box',
  },
  card: {
    backgroundColor: '#FFFFFF',
    width: '100%',
    maxWidth: '1100px',
    borderRadius: '8px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  title: {
    margin: '0 0 20px 0',
    color: '#333333',
    fontSize: '1.8rem',
    fontWeight: 'bold',
  },
  selectRow: {
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  selectLabel: {
    fontWeight: 'bold',
  },
  select: {
    padding: '8px',
    borderRadius: '4px',
    border: '1px solid #ccc',
  },
};
